import type { ApprovalRequest } from '@/lib/types';
import type { GRNCandidate } from './GRNCandidatePicker';

const BLOCKED_AUTH_STATUSES = new Set(['5', '10', '15', '50']);

function candidateValue(c: Record<string, unknown>, ...keys: string[]): unknown {
  for (const key of keys) {
    const value = c[key];
    if (value !== null && value !== undefined && value !== '') return value;
  }
  return null;
}

export function compactCsv(value: unknown): string | null {
  const values = Array.isArray(value) ? value : [value];
  const parts = values
    .flatMap((item) => String(item ?? '').split(','))
    .map((item) => item.trim())
    .filter(Boolean);
  const unique = [...new Set(parts)];
  return unique.length === 0 ? null : unique.join(', ');
}

export function candidatePoNos(c: GRNCandidate): string {
  const value = candidateValue(
    c,
    'order_nos',
    'order_no',
    'po_no',
    'ORDER_NOS',
    'ORDER_NO',
    'PO_NO',
  );
  return compactCsv(value) ?? '—';
}

export function candidateGrnNo(c: GRNCandidate): string {
  return compactCsv(candidateValue(c, 'grn_no', 'GRN_NO')) ?? '—';
}

export function candidateGrnDate(c: GRNCandidate): string | null {
  const value = candidateValue(c, 'grn_date', 'GRN_DATE');
  return value == null ? null : String(value);
}

export function candidateLineId(c: GRNCandidate): string | null {
  const value = candidateValue(
    c,
    'grn_det_id',
    'GRN_DET_ID',
    'grn_detail_id',
    'GRN_DETAIL_ID',
  );
  return value == null ? null : String(value);
}

export function candidateInvoiceRef(c: GRNCandidate): string {
  const value = candidateValue(
    c,
    'invoice_ref_no',
    'INVOICE_REF_NO',
    'detail_invoice_ref_no',
    'DETAIL_INVOICE_REF_NO',
  );
  return value == null ? '' : String(value);
}

export function isAlreadyInvoiced(c: Record<string, unknown>): boolean {
  const explicit = candidateValue(c, 'already_invoiced', 'ALREADY_INVOICED');
  if (explicit !== null) {
    return explicit === true || String(explicit).toLowerCase() === 'true';
  }
  const detailValue = candidateValue(
    c,
    'detail_invoice_count',
    'DETAIL_INVOICE_COUNT',
  );
  if (detailValue !== null) return Number(detailValue ?? 0) >= 1;
  const remaining = candidateValue(c, 'remaining_amount', 'REMAINING_AMOUNT');
  if (remaining !== null && Number(remaining) > 0) return false;
  const value = candidateValue(
    c,
    'invoice_count',
    'INVOICE_COUNT',
    'detail_invoice_count',
    'DETAIL_INVOICE_COUNT',
  );
  return Number(value ?? 0) >= 1;
}

export function hasExplicitAlreadyInvoicedFlag(c: Record<string, unknown>): boolean {
  return candidateValue(c, 'already_invoiced', 'ALREADY_INVOICED') !== null;
}

export function isStaleCandidate(c: Record<string, unknown>): boolean {
  const explicit = candidateValue(c, 'stale_candidate', 'STALE_CANDIDATE');
  return explicit === true || String(explicit ?? '').toLowerCase() === 'true';
}

export function isUnapprovedCandidate(c: Record<string, unknown>): boolean {
  const explicit = candidateValue(c, 'unapproved_candidate', 'UNAPPROVED_CANDIDATE');
  if (explicit !== null) {
    return explicit === true || String(explicit).toLowerCase() === 'true';
  }
  const authStatusId = String(
    candidateValue(c, 'auth_status_id', 'AUTH_STATUS_ID') ?? '',
  ).trim();
  return BLOCKED_AUTH_STATUSES.has(authStatusId);
}

export function candidateAuthStatus(c: Record<string, unknown>): string | null {
  const value = candidateValue(c, 'auth_status', 'AUTH_STATUS');
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}

export function candidateStaleReason(c: GRNCandidate): string | null {
  const value = candidateValue(c, 'stale_reason', 'STALE_REASON');
  return value == null ? null : String(value);
}

export function candidateStalePurchaseInvoiceId(c: GRNCandidate): string | null {
  const value = candidateValue(
    c,
    'stale_purchase_invoice_id',
    'STALE_PURCHASE_INVOICE_ID',
    'purchase_invoice_id',
    'PURCHASE_INVOICE_ID',
  );
  return value == null ? null : String(value);
}

export function candidateSelectionId(c: GRNCandidate): number {
  const value = candidateValue(
    c,
    'candidate_id',
    'CANDIDATE_ID',
    'grn_det_id',
    'GRN_DET_ID',
    'grn_detail_id',
    'GRN_DETAIL_ID',
    'grn_id',
    'GRN_ID',
  );
  const parts = String(value ?? '')
    .split(':')
    .map((part) => Number(part.trim()))
    .filter((part) => Number.isFinite(part));
  return parts.at(-1) ?? Number.NaN;
}

export function candidateAmount(c: GRNCandidate): number {
  const value = candidateValue(
    c,
    'grn_amount',
    'GRN_AMOUNT',
    'grn_line_amount',
    'GRN_LINE_AMOUNT',
    'amount',
    'AMOUNT',
    'remaining_amount',
    'REMAINING_AMOUNT',
  );
  const n = Number(value ?? 0);
  return Number.isFinite(n) ? n : 0;
}

function payloadRecord(req: ApprovalRequest): Record<string, unknown> {
  return (req.payload ?? {}) as Record<string, unknown>;
}

function objectRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function numberValue(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(String(value).replace(/,/g, ''));
  return Number.isFinite(n) ? n : null;
}

function deriveRecommendedGrnNos(req: ApprovalRequest): string[] {
  const payload = payloadRecord(req);
  const sf = objectRecord(payload['structured_fields']);
  const defaults = objectRecord(payload['default_fields']);
  const candidatesRaw = sf['grn_candidates'] ?? payload['grn_candidates'];
  if (!Array.isArray(candidatesRaw)) return [];
  const subtotal = numberValue(
    defaults['subtotal_before_tax'] ?? payload['subtotal_before_tax'] ?? payload['subtotal'],
  );
  const gross = numberValue(
    defaults['gross_amount'] ?? payload['gross_amount'] ?? payload['amount'],
  );
  const targets = [subtotal, gross].filter((v): v is number => v !== null);
  if (targets.length === 0) return [];
  const matches = (candidatesRaw as GRNCandidate[])
    .filter((candidate) => !isAlreadyInvoiced(candidate))
    .filter((candidate) =>
      targets.some((target) => Math.abs(candidateAmount(candidate) - target) < 0.5),
    )
    .map(candidateGrnNo)
    .filter((value) => value !== '—');
  const uniqueMatches = [...new Set(matches)];
  return uniqueMatches.length === 1 ? uniqueMatches : [];
}

// Slice 4 Task 4.3: read the recommended GRN numbers straight from the v1
// `structured_fields.recommended_grn_nos` list. The legacy regex-scrape over
// blurb/error-message strings is gone; N8N now emits this list explicitly per
// the bucket contract (automations/ap-purchase-invoice/bucket-contract.md §5 MULTIPLE_GRNS).
export function recommendedGrnNos(req: ApprovalRequest): string[] {
  const payload = payloadRecord(req);
  const sf = payload['structured_fields'];
  const list =
    sf && typeof sf === 'object' && !Array.isArray(sf)
      ? (sf as Record<string, unknown>)['recommended_grn_nos']
      : null;
  const explicit = Array.isArray(list)
    ? list.map((v) => String(v).trim()).filter(Boolean)
    : [];
  return explicit.length > 0 ? explicit : deriveRecommendedGrnNos(req);
}
